<template>
  <div>
    <!-- Stat strip + entry-point actions -->
    <div class="flex flex-wrap justify-space-between align-items-center gap-m">
      <div v-if="showStats" class="flex gap-xl">
        <div>
          <h2 class="dashboard-stat-label">
            Available passes
          </h2>
          <div class="dashboard-stat-value">
            {{ loadingAvailablePasses ? '—' : available }}
          </div>
        </div>
        <div>
          <h2 class="dashboard-stat-label">
            Used passes
          </h2>
          <div class="dashboard-stat-value">
            {{ loadingAvailablePasses ? '—' : used }}
          </div>
        </div>
      </div>
      <spinner v-if="loadingAvailablePasses"></spinner>
      <div class="flex gap-xs" v-if="!showAddForm && !showUseForm && !loadingAvailablePasses">
        <button @click.prevent="showUseForm = true" class="btn btn-default" :disabled="available === 0"><i class="fa-regular fa-calendar-check"></i> Use pass</button>
        <button @click.prevent="showAddForm = true" class="btn btn-primary"><i class="fa-solid fa-plus"></i> Add pass</button>
      </div>
    </div>

    <!-- Add a free pass -->
    <div v-if="showAddForm" class="archive-form should-dim modify-form m-t-md card-bordered p-m">
      <h3 class="font-heavy m-t-none m-b-xxs">Add a free class pass for {{ wrestlerName }}</h3>
      <p class="text-gray-600 m-b-none">
        This won't charge {{ wrestlerName }} or their parent.
      </p>
      <div class="form-group m-t-md">
        <label class="font-normal m-b-xxs">Which registration should this pass be tied to?</label>
        <select v-model="paidSessionId" class="form-control">
          <option v-for="session in availableDropInSessions" v-bind:value="session.id">
            {{ session.name }}
          </option>
        </select>
      </div>
      <div class="form-group m-t-md">
        <label class="font-normal m-b-xxs">Expiry date <span class="text-gray-600 font-normal">(optional)</span></label>
        <div>
          <date-picker :date="expiresAt" :disabled-date="disabledBeforeToday" placeholder="No expiry" @input="expiresAt = $event"></date-picker>
        </div>
        <p class="text-gray-600 m-t-xs text-14 m-b-none">
          Leave blank for a pass that never expires. It becomes unusable at the end of the selected day.
        </p>
      </div>
      <div class="actions m-t-md flex gap-xs justify-end">
        <button v-on:click="hideForms" class="btn btn-default">Cancel</button>
        <ladda-button @lbClicked="addPass" el-class="btn-primary" :loading="loading">
          Add pass
        </ladda-button>
      </div>
    </div>

    <!-- Redeem an available pass -->
    <div v-if="showUseForm" class="archive-form should-dim modify-form m-t-md card-bordered p-m">
      <div class="text-semi">
        Use a class pass for {{ wrestlerName }}?
      </div>
      <p class="text-gray-600 m-t-xs text-14">
        This redeems one of {{ wrestlerName }}'s available passes and counts as a check-in for a class.
      </p>
      <div class="actions m-t-md flex gap-xs justify-end">
        <button v-on:click="hideForms" class="btn btn-default">Cancel</button>
        <ladda-button @lbClicked="usePass" el-class="btn-primary" :loading="loading">
          Use pass
        </ladda-button>
      </div>
    </div>

    <p v-if="error" class="text-danger m-t-sm">{{ errorMessage }}</p>
  </div>
</template>
<script>
import Spinner from './spinner.vue';
import DatePicker from './date_picker.vue';
import LaddaButton from '../../shared/ladda_button.vue';

export default {
  name: 'class-pass-manager',
  components: { LaddaButton, Spinner, DatePicker },
  props: {
    availableCount: {
      type: Number,
      required: true,
    },
    usedCount: {
      type: Number,
      required: true,
    },
    wrestlerId: {
      type: Number,
      required: true,
    },
    wrestlerName: {
      type: String,
      required: true,
    },
    adminName: {
      type: String,
      required: true,
    },
    // The wrestler Payments tab shows its own balance (grouped by source), so
    // it only needs the Use / Add actions and their forms.
    showStats: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      available: 0,
      used: 0,
      showUseForm: false,
      showAddForm: false,
      error: false,
      errorMessage: '',
      loading: false,
      loadingAvailablePasses: false,
      loadingAvailableSessions: false,
      availablePasses: [],
      paidSessionId: null,
      availableDropInSessions: [],
      // moment object (in the admin's local tz) or null for no expiry
      expiresAt: null,
    };
  },
  created() {
    this.available = this.availableCount;
    this.used = this.usedCount;
    this.loadAvailablePasses();
    this.loadAvailableSessions();
  },
  methods: {
    loadAvailablePasses() {
      const vm = this;
      vm.loadingAvailablePasses = true;

      const url = vm.$apiService.availableWrestlerClassPasses(vm.wrestlerId);
      vm.$apiService.loadAllPages(url, 'class_passes')
        .then((class_passes) => {
          vm.availablePasses = class_passes;
          vm.loadingAvailablePasses = false;
          vm.available = vm.availablePasses.length;
        })
        .catch((error) => {
          vm.loadingAvailablePasses = false;
          vm.errorMessage = `Error retrieving available passes ${error.toString()}`;
          vm.error = true;
        });
    },
    loadAvailableSessions() {
      const vm = this;
      vm.loadingAvailableSessions = true;
      const url = `${vm.$apiService.paidSessionsUrl()}?type=trial_or_dropin`;
      vm.$apiService.loadAllPages(url, 'paid_sessions')
        .then((paid_sessions) => {
          vm.availableDropInSessions = paid_sessions;
          if (vm.availableDropInSessions.length > 0) {
            vm.paidSessionId = _.head(vm.availableDropInSessions).id;
          }
          vm.loadingAvailableSessions = false;
        })
        .catch((error) => {
          vm.errorMessage = `Error retrieving available sessions ${error.toString()}`;
          vm.error = true;
        });
    },
    hideForms() {
      this.showAddForm = false;
      this.showUseForm = false;
      this.error = false;
      this.expiresAt = null;
    },
    // Disable any day before today — an expiry in the past would make the pass
    // unusable the moment it's created.
    disabledBeforeToday(date) {
      return moment(date).isBefore(moment(), 'day');
    },
    addPass() {
      if (this.loading) {
        return;
      }

      this.loading = true;

      const tz = moment.tz.guess();
      // Treat the picked day as "expires at the end of that day" to match how
      // trial/drop-in and subscription passes expire elsewhere in the system.
      const expiresAt = this.expiresAt ? this.expiresAt.clone().endOf('day').toISOString() : null;
      const params = {
        class_pass: {
          status: 'available',
          log: `Free pass added to ${this.wrestlerName} by ${this.adminName} at ${moment().tz(tz).format('M/D/YYYY hh:mm a')}`,
          paid_session_id: this.paidSessionId,
          expires_at: expiresAt,
        },
      };
      const url = this.$apiService.wrestlerClassPasses(this.wrestlerId);
      axios.post(url, params)
        .then((response) => {
          this.$notificationManager.emit('show-toast', 'Pass added', true);
          this.$notificationManager.emit('class-pass-changed');
          this.loading = false;
          this.hideForms();
          this.loadAvailablePasses();
        })
        .catch((error) => {
          this.error = true;
          this.errorMessage = `Error adding pass ${error.toString()}`;
          this.loading = false;
        });
    },
    usePass() {
      const pass = _.head(this.availablePasses);
      if (this.loading || !pass) {
        return;
      }

      this.loading = true;

      const tz = moment.tz.guess();
      const params = {
        class_pass: {
          status: 'used',
          log: `${pass.log} Pass for ${this.wrestlerName} redeemed by ${this.adminName} at ${moment().tz(tz).format('M/D/YYYY hh:mm a')}`,
        },
      };
      const url = this.$apiService.wrestlerClassPass(this.wrestlerId, pass.id);
      axios.put(url, params)
        .then((response) => {
          this.$notificationManager.emit('show-toast', 'Pass used', true);
          this.$notificationManager.emit('class-pass-changed');
          this.loading = false;
          this.hideForms();
          this.loadAvailablePasses();
          this.used += 1;
        })
        .catch((error) => {
          this.error = true;
          this.errorMessage = `Error using pass ${error.toString()}`;
          this.loading = false;
        });
    },
  },
};
</script>
